import {FC} from 'react'
import {Routes, Route, BrowserRouter, Navigate, Outlet, useParams, useSearchParams} from 'react-router-dom'
import {PrivateRoutes} from './PrivateRoutes'
import {ErrorsPage} from '../modules/errors/ErrorsPage'
import {Logout, AuthPage, useAuth} from '../modules/auth'
import App from '../App'
import {MasterLayout} from '../../_metronic/layout/MasterLayout'
import {InterpreterInvitationResponse} from '../pages/InterpreterInvitationResponse'
import {CheckInApprovalPage} from '../pages/CheckInApprovalPage'
import {DigitalVosViewPage} from '../pages/DigitalVosViewPage'
import {VOSLinkRouter} from '../UI/appointment-management/components/VOSLinkRouter'
import {BulkVosPublicPage} from '../pages/BulkVosPublicPage'

/**
 * Base URL of the website.
 *
 * @see https://facebook.github.io/create-react-app/docs/using-the-public-folder
 */
const {PUBLIC_URL} = process.env

/** Public layout for VOS confirm/dispute – no sidebar, no navbar. Used so email links work without login. */
const VOSPublicLayout = () => <Outlet />

/** Redirect old OSI link (private path) to public VOS confirm page so it works without login. */
const RedirectOsiConfirm: FC = () => {
  const {id} = useParams<{id: string}>()
  const [search] = useSearchParams()
  const qs = search.toString()
  return <Navigate to={`/vos/confirm/${id ?? ''}${qs ? `?${qs}` : ''}`} replace />
}

/** Redirect old OSI link (private path) to public VOS dispute page so it works without login. */
const RedirectOsiDispute: FC = () => {
  const {id} = useParams<{id: string}>()
  const [search] = useSearchParams()
  const qs = search.toString()
  return <Navigate to={`/vos/dispute/${id ?? ''}${qs ? `?${qs}` : ''}`} replace />
}

const AppRoutes: FC = () => {
  const {currentUser} = useAuth()
  return (
    <BrowserRouter basename={PUBLIC_URL}>
      <Routes>
        <Route element={<App />}>
          <Route element={<MasterLayout />}>
            <Route path='error/*' element={<ErrorsPage />} />
          </Route>
          <Route path='logout' element={<Logout />} />
          <Route path='interpreter-invitation-response' element={<InterpreterInvitationResponse />} />
          <Route path='check-in-approval' element={<CheckInApprovalPage />} />
          <Route path='digital-vos/view/:appointmentId' element={<DigitalVosViewPage />} />
          {/* VOS: fully public (no auth). No sidebar/navbar – only the form. Listed first so /vos/* is never caught by path='*'. */}
          <Route path='vos' element={<VOSPublicLayout />}>
            <Route path='confirm/:id' element={<VOSLinkRouter mode='confirm' />} />
            <Route path='dispute/:id' element={<VOSLinkRouter mode='dispute' />} />
          </Route>
          <Route path='bulk-vos/:token' element={<BulkVosPublicPage />} />
          {/* Old OSI links (appointmentViewer/.../osi/confirm|dispute): redirect to public VOS so they work without login. */}
          <Route path='appointmentViewer/:id/osi/confirm' element={<RedirectOsiConfirm />} />
          <Route path='appointmentViewer/:id/osi/dispute' element={<RedirectOsiDispute />} />
          <Route path='interpreter-invitation-response' element={<InterpreterInvitationResponse />} />
          {currentUser ? (
            <>
              <Route path='/*' element={<PrivateRoutes />} />
              <Route index element={<Navigate to='/dashboard' />} />
            </>
          ) : (
            <>
              <Route path='auth/*' element={<AuthPage />} />
              <Route path='*' element={<Navigate to='/auth' />} />
            </>
          )}
        </Route>
      </Routes>
    </BrowserRouter>
  )
}

export {AppRoutes}
